/**
 * Testimonials Page - CPR Training Direct
 *
 * Displays approved customer testimonials in a masonry-style grid.
 * Includes stats section and CTA for submitting reviews.
 * SEO optimized for CPR training reviews and testimonials.
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { TestimonialCard } from '../../components/cpr/TestimonialCard';
import { CTABanner } from '../../components/cpr/CTABanner';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { getApprovedTestimonials } from '../../data/testimonials';

const Testimonials: React.FC = () => {
  // Filter to show only approved testimonials
  const approvedTestimonials = getApprovedTestimonials();

  // Calculate stats (guarded against divide-by-zero when there are no testimonials)
  const totalReviews = approvedTestimonials.length;
  const averageRating =
    totalReviews === 0
      ? 0
      : approvedTestimonials.reduce((sum, t) => sum + t.rating, 0) / totalReviews;
  const fiveStarReviews = approvedTestimonials.filter((t) => t.rating === 5).length;
  const fiveStarPercentage =
    totalReviews === 0 ? 0 : Math.round((fiveStarReviews / totalReviews) * 100);

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        {/* Primary Meta Tags */}
        <title>CPR Training Reviews | What Our Students Say</title>
        <meta name="title" content="CPR Training Reviews | What Our Students Say" />
        <meta
          name="description"
          content="Read reviews from certified students in Orlando and Atlanta. Professional AHA-certified CPR training in Central Florida and Georgia."
        />
        <meta
          name="keywords"
          content="CPR training reviews, student testimonials, certification feedback, Orlando CPR reviews"
        />
        <meta name="author" content="CPR Training Direct" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://cprtrainingdirect.com/testimonials" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cprtrainingdirect.com/testimonials" />
        <meta property="og:title" content="CPR Training Reviews | What Our Students Say" />
        <meta
          property="og:description"
          content="Read reviews from certified students in Orlando and Atlanta. Professional AHA-certified CPR training in Central Florida and Georgia."
        />
        <meta property="og:image" content="https://cprtrainingdirect.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="CPR Training Direct" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://cprtrainingdirect.com/testimonials" />
        <meta name="twitter:title" content="CPR Training Reviews | What Our Students Say" />
        <meta
          name="twitter:description"
          content="Read reviews from certified students in Orlando and Atlanta. Professional AHA-certified CPR training in Central Florida and Georgia."
        />
        <meta name="twitter:image" content="https://cprtrainingdirect.com/og-image.jpg" />

        {/* Geo Tags for Local SEO */}
        <meta name="geo.region" content="US-FL" />
        <meta name="geo.placename" content="Orlando" />
        <meta name="geo.position" content="28.5383;-81.3792" />
        <meta name="ICBM" content="28.5383, -81.3792" />
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* HERO SECTION - Brutalist */}
        <section className="bg-indigo_dye-500 text-white py-16 md:py-24 px-6">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="font-display text-6xl md:text-7xl lg:text-8xl font-bold uppercase tracking-wider mb-6">
              What Our Students Say
            </h1>
            <p className="font-body text-xl md:text-2xl text-indigo_dye-100 leading-relaxed max-w-3xl mx-auto">
              Real reviews from people we've trained to save lives
            </p>
          </div>
        </section>

        {/* STATS SECTION - Brutalist Cards with AOS */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid gap-8 md:grid-cols-3">
              {/* Total Reviews */}
              <div data-aos="zoom-in" data-aos-delay="0">
                <Card variant="brutalist" className="text-center">
                  <div className="font-display text-5xl md:text-6xl font-extrabold text-amaranth_purple-500 mb-4">
                    {totalReviews}
                  </div>
                  <p className="font-body text-lg text-gray-700 uppercase tracking-wide font-semibold">
                    Total Reviews
                  </p>
                </Card>
              </div>

              {/* Average Rating */}
              <div data-aos="zoom-in" data-aos-delay="150">
                <Card variant="brutalist" className="text-center">
                  <div className="font-display text-5xl md:text-6xl font-extrabold text-amaranth_purple-500 mb-4">
                    {averageRating.toFixed(1)}
                    <span className="text-3xl text-yellow-400 ml-2">★</span>
                  </div>
                  <p className="font-body text-lg text-gray-700 uppercase tracking-wide font-semibold">
                    Average Rating
                  </p>
                </Card>
              </div>

              {/* 5-Star Reviews */}
              <div data-aos="zoom-in" data-aos-delay="300">
                <Card variant="brutalist" className="text-center">
                  <div className="font-display text-5xl md:text-6xl font-extrabold text-amaranth_purple-500 mb-4">
                    {fiveStarPercentage}%
                  </div>
                  <p className="font-body text-lg text-gray-700 uppercase tracking-wide font-semibold">
                    5-Star Reviews
                  </p>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS GRID - Brutalist */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2
              data-aos="fade-up"
              className="font-display text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-wider text-center mb-12 text-indigo_dye-500"
            >
              Student Reviews
            </h2>
            {/* Masonry-style grid using CSS columns */}
            <div className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8">
              {approvedTestimonials.map((testimonial, index) => (
                <div
                  key={testimonial.id}
                  className="break-inside-avoid"
                  data-aos="fade-up"
                  data-aos-delay={Math.min(index * 50, 400)}
                >
                  <TestimonialCard testimonial={testimonial} />
                </div>
              ))}
            </div>

            {/* Empty State (shouldn't happen with seeded data) */}
            {approvedTestimonials.length === 0 && (
              <div className="text-center py-16">
                <p className="font-body text-gray-500 text-lg">No testimonials available at this time.</p>
              </div>
            )}
          </div>
        </section>

        {/* SUBMIT REVIEW CTA - Brutalist */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-wider mb-6 text-indigo_dye-500">
              Completed a Course with Us?
            </h2>
            <p className="font-body text-lg md:text-xl text-gray-600 mb-12 leading-relaxed max-w-2xl mx-auto">
              We'd love to hear about your experience! Your feedback helps us improve and helps
              others make informed decisions about their CPR training.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              {/* TODO: replace with real Google review link, then re-enable */}
              <Button variant="primary" size="xl" disabled title="Coming soon">
                Leave a Google Review
              </Button>
              <Button
                variant="outline"
                size="xl"
                onClick={() => (window.location.href = '/contact')}
              >
                Share Your Feedback
              </Button>
            </div>
          </div>
        </section>

        {/* CTA BANNER */}
        <CTABanner
          title="Join Our Growing Community of Certified Life-Savers"
          subtitle="Experience the training that earns rave reviews. Book your CPR certification class today."
          buttonText="Book Your Class"
          buttonLink="/book"
        />
      </div>
    </>
  );
};

export default Testimonials;
